import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { fetchAdminBlogList } from '@/api/blog';
import { BlogPostAdmin } from '@/api/schemas';
import Spinner from '@/components/common/Spinner';
import BlogEditorForm from './BlogEditorForm';

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

type ViewMode =
  | { mode: 'list' }
  | { mode: 'editor'; postId: number | null };

const BlogAdminPanel: React.FC = () => {
  const { token } = useAuth();
  const [view, setView] = useState<ViewMode>({ mode: 'list' });

  const { data: posts = [], isLoading, error } = useQuery({
    queryKey: ['admin-blog-list'],
    queryFn: () => fetchAdminBlogList(token),
    enabled: !!token && view.mode === 'list',
    refetchOnWindowFocus: false,
  });

  if (view.mode === 'editor') {
    return (
      <BlogEditorForm
        postId={view.postId}
        onClose={() => setView({ mode: 'list' })}
      />
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-heading font-bold text-accent-yellow">Blog Posts</h2>
        <button
          onClick={() => setView({ mode: 'editor', postId: null })}
          className="bg-accent-yellow hover:bg-yellow-500 text-primary font-bold py-2 px-4 rounded-md transition-colors"
        >
          + New Post
        </button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      )}

      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 text-red-300">
          Failed to load posts.
        </div>
      )}

      {!isLoading && !error && posts.length === 0 && (
        <div className="text-center py-12 text-gray-500 italic">
          No posts yet. Click "New Post" to create your first one.
        </div>
      )}

      {!isLoading && !error && posts.length > 0 && (
        <div className="bg-surface-card rounded-lg border border-gray-700/50 overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-primary-dark border-b border-gray-700">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Title</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Type</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Status</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Published</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Created</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {posts.map((post: BlogPostAdmin) => {
                const isPublished = !!post.published_at;
                const hasPreview = post.preview_contest_id != null;
                const hasRecap = post.recap_contest_id != null;
                const typeLabel = hasPreview && hasRecap
                  ? 'PREVIEW + RECAP'
                  : hasPreview
                    ? 'PREVIEW'
                    : hasRecap
                      ? 'RECAP'
                      : 'EDITORIAL';
                return (
                  <tr key={post.id} className="hover:bg-primary/40 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-200">{post.title}</div>
                      <div className="text-xs text-gray-500 font-mono">{post.slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded bg-accent-yellow/15 text-accent-yellow text-badge font-semibold tracking-wider">
                        {typeLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {isPublished ? (
                        <span className="px-2 py-0.5 rounded bg-green-500/20 text-green-300 text-xs font-semibold">
                          PUBLISHED
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded bg-gray-500/20 text-gray-300 text-xs font-semibold">
                          DRAFT
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{formatDate(post.published_at)}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{formatDate(post.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setView({ mode: 'editor', postId: post.id })}
                        className="text-accent-light-blue hover:underline text-sm mr-3"
                      >
                        Edit
                      </button>
                      {isPublished && (
                        <a
                          href={`/blog/${post.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-gray-200 hover:underline text-sm"
                        >
                          View
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BlogAdminPanel;
